import React, { PropsWithChildren } from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/legacy/image'
import { Container } from '@material-ui/core'
import type { ReactPlayerProps } from 'react-player'
import { SecondaryPageBannerFragment } from '@gql'
import { isNotNull } from '../../utils/nonNull'
import { SecondaryBanner } from './SecondaryBanner'
import {
  HeroAlign,
  HeroBreadcrumbs,
  HeroContent,
  HeroImageWrap,
  HeroInner,
  HeroScrim,
  HeroSection,
  HeroText,
  HeroTitle,
  HeroVideoWrap,
} from './StyledSecondaryBannerHero'

// Lazy/dynamic so react-player is only loaded when a video banner actually
// renders — keeps it out of every other page's main bundle.
const ReactPlayer = dynamic<ReactPlayerProps>(() => import('react-player/lazy'), { ssr: false })

type LinkFieldButton = SecondaryPageBannerFragment['primaryButton']

export type SecondaryBannerHeroProps = {
  title?: string | null
  subTitle?: string | null
  src?: string | null
  videoLink?: string | null
  align?: HeroAlign
  breadcrumbs?: React.ReactNode
  primaryButton?: LinkFieldButton
  secondaryButton?: LinkFieldButton
  // Secondary Button Type (CMS lightswitch): false = Dark (as-is), true = Light
  // (white text + white outline over the photo, matching the design).
  lightSecondaryButton?: boolean
  // Forwarded so the parent can attach sibling-spacing styles via styled().
  className?: string
}

export const SecondaryBannerHero: React.FC<PropsWithChildren<SecondaryBannerHeroProps>> = ({
  title,
  subTitle,
  src,
  videoLink,
  align = 'left',
  breadcrumbs,
  primaryButton,
  secondaryButton,
  lightSecondaryButton = false,
  className,
}) => {
  const hasVideo = isNotNull(videoLink) && videoLink !== ''

  return (
    <HeroSection $align={align} className={className}>
      {src && (
        <HeroImageWrap>
          <Image src={src} alt="" layout="fill" objectFit="cover" objectPosition="center" priority />
        </HeroImageWrap>
      )}
      {hasVideo && (
        <HeroVideoWrap>
          <ReactPlayer
            url={videoLink as string}
            playing
            loop
            muted
            controls={false}
            width="100%"
            height="100%"
            config={{
              youtube: { playerVars: { controls: 0, modestbranding: 1, rel: 0 } },
              vimeo: { playerOptions: { background: true, controls: false } },
            }}
          />
        </HeroVideoWrap>
      )}
      <HeroScrim $align={align} />
      <Container maxWidth="lg">
        <HeroInner $align={align}>
          <HeroContent $lightSecondary={lightSecondaryButton}>
            {breadcrumbs && <HeroBreadcrumbs>{breadcrumbs}</HeroBreadcrumbs>}
            {title && <HeroTitle variant="h1">{title}</HeroTitle>}
            {subTitle && <HeroText variant="body1">{subTitle}</HeroText>}
            {(primaryButton || secondaryButton) && (
              <SecondaryBanner section>
                {primaryButton && primaryButton.__typename === 'linkField_Link' && isNotNull(primaryButton.url) && (
                  <SecondaryBanner
                    cta
                    buttonProps={{
                      cta: true,
                      target: primaryButton?.target === '_blank' ? '_blank' : '_self',
                    }}
                    href={primaryButton.url || '#'}
                  >
                    {primaryButton.customText}
                  </SecondaryBanner>
                )}
                {secondaryButton && secondaryButton.__typename === 'linkField_Link' && isNotNull(secondaryButton.url) && (
                  <SecondaryBanner
                    cta
                    buttonProps={{
                      primary: true,
                      buttonColor: 'black',
                      target: secondaryButton?.target === '_blank' ? '_blank' : '_self',
                      size: 'small',
                    }}
                    href={secondaryButton.url || '#'}
                  >
                    {secondaryButton.customText}
                  </SecondaryBanner>
                )}
              </SecondaryBanner>
            )}
          </HeroContent>
        </HeroInner>
      </Container>
    </HeroSection>
  )
}
